import { GatewayQuestionnaire } from "@conformite/gateway";
import { useQuestionnaireDefinitionQuery } from "@src/data/questionnaire/hook/useQuestionnaireDefinitionQuery";
import { useQuestionnaireLatestInstanceQuery } from "@src/data/questionnaire/hook/useQuestionnaireLatestInstanceQuery";
import { useSaveQuestionnaireInstanceMutation } from "@src/data/questionnaire/hook/useSaveQuestionnaireInstanceMutation";
import { useCallback, useEffect, useState } from "react";
import { AiAnswer } from "../QuestionnaireQuestion/QuestionnaireQuestion";

type AnswersMap = Record<string, string>;
type AiAnswersMap = Map<string, AiAnswer>;

export type QuestionnairePrefill = {
  questionOrder: number;
  optionLabel: string;
};

type QuestionnairePrefillOptions = {
  prefill?: QuestionnairePrefill[];
  isPrefillLoading?: boolean;
};

function applyPrefill(
  answerMap: AnswersMap,
  definition: GatewayQuestionnaire.QuestionnaireDefinitionResponse,
  prefill?: QuestionnairePrefill[]
): AnswersMap {
  if (!prefill?.length) return answerMap;
  const questionByOrder = new Map(
    definition.questions.map((q) => [q.order, q])
  );
  prefill.forEach(({ questionOrder, optionLabel }) => {
    const question = questionByOrder.get(questionOrder);
    if (!question || answerMap[question.id]) return;
    const option = question.options.find((o) => o.label === optionLabel);
    if (!option) return;
    answerMap[question.id] = option.id;
  });
  return answerMap;
}

export function useQuestionnaireForm(
  slug: GatewayQuestionnaire.QuestionnaireSlug | undefined,
  contextType: string,
  contextId: string,
  prefillOptions?: QuestionnairePrefillOptions
) {
  const { data: definition, isLoading: isLoadingDefinition } =
    useQuestionnaireDefinitionQuery(slug);

  const { data: latestInstance, isLoading: isLoadingInstance } =
    useQuestionnaireLatestInstanceQuery(contextType, contextId, slug);

  const { mutateAsync: saveInstance, isPending: isSavingInstance } =
    useSaveQuestionnaireInstanceMutation();

  const [answers, setAnswers] = useState<AnswersMap>({});
  const [comment, setComment] = useState("");
  const [persistedAiAnswers, setPersistedAiAnswers] = useState<AiAnswersMap>(
    new Map()
  );
  const [initialized, setInitialized] = useState(false);
  const [hasSavedInstance, setHasSavedInstance] = useState(false);

  const prefill = prefillOptions?.prefill;
  const isPrefillLoading = prefillOptions?.isPrefillLoading ?? false;

  useEffect(() => {
    if (initialized) return;
    if (!definition || isPrefillLoading) return;
    if (latestInstance) {
      const labelToCurrentId = new Map<string, string>();
      definition.questions.forEach((q) => {
        labelToCurrentId.set(q.label, q.id);
      });
      const answerMap: AnswersMap = {};
      const aiMap: AiAnswersMap = new Map();
      latestInstance.answers.forEach((a) => {
        const currentId =
          labelToCurrentId.get(a.label) ?? a.questionDefinitionId;
        answerMap[currentId] = a.value;
        if (a.aiAnswer) {
          aiMap.set(currentId, {
            answer: a.aiAnswer.answer,
            justification: a.aiAnswer.justification ?? "",
            evidence: a.aiAnswer.evidence,
            confidence: a.aiAnswer.confidence,
            guardrail: a.aiAnswer.guardrail,
            evidenceGrounded: a.aiAnswer.evidenceGrounded,
          });
        }
      });
      applyPrefill(answerMap, definition, prefill);
      setAnswers(answerMap);
      setPersistedAiAnswers(aiMap);
      setComment(latestInstance.comment ?? "");
      setHasSavedInstance(true);
      setInitialized(true);
    } else if (!isLoadingInstance) {
      setAnswers(applyPrefill({}, definition, prefill));
      setInitialized(true);
    }
  }, [
    latestInstance,
    definition,
    isLoadingInstance,
    initialized,
    isPrefillLoading,
    prefill,
  ]);

  const save = useCallback(
    async (
      formAnswers?: AnswersMap,
      formComment?: string,
      aiAnswers?: AiAnswersMap
    ) => {
      if (!definition || !slug) return;

      const answersToSave = formAnswers ?? answers;
      const commentToSave = formComment ?? comment;
      const body: GatewayQuestionnaire.SaveQuestionnaireInstanceRequestType = {
        questionnaireSlug: slug,
        contextType,
        contextId,
        comment: commentToSave || undefined,
        answers: Object.entries(answersToSave)
          .filter(([, value]) => Boolean(value))
          .map(([questionDefinitionId, value]) => {
            const ai = aiAnswers?.get(questionDefinitionId);
            return {
              questionDefinitionId,
              value,
              aiAnswer: ai
                ? {
                    answer: ai.answer,
                    justification: ai.justification || undefined,
                    evidence: ai.evidence,
                    confidence: ai.confidence,
                    guardrail: ai.guardrail,
                    evidenceGrounded: ai.evidenceGrounded,
                  }
                : undefined,
            };
          }),
      };

      await saveInstance(body);
    },
    [definition, slug, contextType, contextId, comment, answers, saveInstance]
  );

  return {
    definition,
    answers,
    comment,
    setComment,
    persistedAiAnswers,
    save,
    isLoading: isLoadingDefinition || isLoadingInstance || !initialized,
    isSaving: isSavingInstance,
    hasSavedInstance,
  };
}
